import { Component, computed, inject } from '@angular/core';
import { AnalysisRowType } from '@api-response-types/analysis-version-group.response';
import { AnalysisCalculationLevel } from '@api-types/enums/analysis-calculation-level.enum';
import { AnalysisType } from '@api-types/enums/analysis-type.enum';
import { AnalysisVersionStatus } from '@api-types/enums/analysis-version-status.enum';
import { AnalysisConfigurationResponse } from '@api-response-types/analysis-configuration.response';
import { DateUtilsService } from '@core/utils/date-utils.service';
import { TwoDecimalPercentPipe } from '@core/utils/two-decimal-percent.pipe';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { differenceInCalendarMonths } from 'date-fns';
import { AnalysisStateService } from 'demand-forecasting/analysis/services/analysis-state.service';
import { PmKpiCardComponent } from '@libs/ui';

type MonthCoverage = { covered: number; total: number };

const CALCULATION_LEVEL_KPI_KEYS: Record<
  AnalysisCalculationLevel,
  { labelKey: string; tooltipKey: string }
> = {
  [AnalysisCalculationLevel.LOWEST]: {
    labelKey: 'ANALYSIS.CALCULATION_LEVEL.LOWEST_CHIP',
    tooltipKey: 'ANALYSIS.CALCULATION_LEVEL.LOWEST_DESCRIPTION',
  },
  [AnalysisCalculationLevel.HIGHEST]: {
    labelKey: 'ANALYSIS.CALCULATION_LEVEL.HIGHEST_CHIP',
    tooltipKey: 'ANALYSIS.CALCULATION_LEVEL.HIGHEST_DESCRIPTION',
  },
};

@Component({
  selector: 'pm-analysis-version-metadata',
  templateUrl: './analysis-version-metadata.component.html',
  providers: [TwoDecimalPercentPipe],
  imports: [TranslateModule, PmKpiCardComponent],
})
export class AnalysisVersionMetadataComponent {
  protected AnalysisVersionStatus = AnalysisVersionStatus;
  protected AnalysisType = AnalysisType;
  protected analysisStateService = inject(AnalysisStateService);
  protected readonly dateUtilsService = inject(DateUtilsService);
  private readonly translate = inject(TranslateService);

  protected cycleKpiValue(
    configuration: AnalysisConfigurationResponse,
  ): string {
    const name = configuration.anchorConsensusCycleName;
    if (!name) {
      return '/';
    }
    const closed = this.translate.instant(
      'ANALYSIS.CREATE_CONFIGURATION.CLOSED',
      {
        date: this.dateUtilsService.formatDateBasedOnLanguage(
          configuration.anchorConsensusCycleFinishedAt ?? undefined,
        ),
      },
    );
    return `${name} - ${closed}`;
  }

  protected readonly calculationLevelKpi = computed(() => {
    const calculationLevel =
      this.analysisStateService.selectedVersion()?.calculationLevel;
    if (!calculationLevel) {
      return undefined;
    }

    return CALCULATION_LEVEL_KPI_KEYS[calculationLevel];
  });

  protected readonly timePeriodTooltip = computed(
    (): { key: string; horizon: string } | undefined => {
      const version = this.analysisStateService.selectedVersion();
      if (!version) {
        return undefined;
      }

      const horizon =
        this.dateUtilsService.formatMonthYearBasedOnLanguage(version.minDate) +
        ' - ' +
        this.dateUtilsService.formatMonthYearBasedOnLanguage(version.maxDate);
      const hasCycle = version.anchorConsensusCycleId !== null;
      const hasForecastVersion = version.anchorForecastVersionId !== null;

      if (version.analysisType !== AnalysisType.SINGLE_PERIOD) {
        return { key: 'ANALYSIS.TIME_PERIOD_TOOLTIP_ROLLING', horizon };
      }
      if (hasCycle && hasForecastVersion) {
        return { key: 'ANALYSIS.TIME_PERIOD_TOOLTIP_SINGLE', horizon };
      }
      if (hasCycle) {
        return { key: 'ANALYSIS.TIME_PERIOD_TOOLTIP_CYCLE', horizon };
      }
      return { key: 'ANALYSIS.TIME_PERIOD_TOOLTIP_VERSION', horizon };
    },
  );

  protected readonly timePeriod = computed(
    ():
      | { start: string; end: string; coverage?: MonthCoverage }
      | undefined => {
      const version = this.analysisStateService.selectedVersion();
      if (!version) {
        return undefined;
      }
      if (version.analysisType !== AnalysisType.SINGLE_PERIOD) {
        return { start: version.minDate, end: version.maxDate };
      }

      const versionGroup = this.analysisStateService.aggregatedVersionGroup;
      if (!versionGroup.hasValue()) {
        return undefined;
      }
      const months = versionGroup
        .value()
        .flatMap((row) =>
          row.rowType === AnalysisRowType.TIMEPOINT ? [row.tsDate] : [],
        )
        .sort((a, b) => a.localeCompare(b));

      return {
        start: months.at(0) ?? version.minDate,
        end: months.at(-1) ?? version.maxDate,
        coverage: {
          covered: new Set(months).size,
          total:
            differenceInCalendarMonths(
              new Date(version.maxDate),
              new Date(version.minDate),
            ) + 1,
        },
      };
    },
  );
}
